'use client';

import { Bar } from 'react-chartjs-2';
import { Box, Grid, Typography } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type TooltipItem,
} from 'chart.js';

import { StyledTableContainer } from '~/shared';
import type { MonitoringChartData } from '~/types/monitoring';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface MonitoringChartsProps {
  data: MonitoringChartData;
  impressionsStatus: 'CRITICO' | 'ATENCAO' | 'NORMAL';
  clicksStatus: 'CRITICO' | 'ATENCAO' | 'NORMAL';
}

export const MonitoringCharts = ({
  data,
  impressionsStatus,
  clicksStatus,
}: MonitoringChartsProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CRITICO':
        return '#EF5350';
      case 'ATENCAO':
        return '#FFA726';
      default:
        return '#66BB6A';
    }
  };

  const impressionsChartData = {
    labels: data.labels,
    datasets: [
      {
        label: 'Impressões',
        data: data.impressions,
        backgroundColor: getStatusColor(impressionsStatus),
        borderRadius: 4,
      },
    ],
  };

  const clicksChartData = {
    labels: data.labels,
    datasets: [
      {
        label: 'Cliques',
        data: data.clicks,
        backgroundColor: getStatusColor(clicksStatus),
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context: TooltipItem<'bar'>) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${(value ?? 0).toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number | string) => {
            return Number(value).toLocaleString('pt-BR');
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <StyledTableContainer
            sx={{
              p: 2,
              height: 300,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}
            >
              Impressões
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Bar data={impressionsChartData} options={chartOptions} />
            </Box>
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Status da Fila:
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: getStatusColor(impressionsStatus),
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'white', fontWeight: 600 }}
                >
                  {impressionsStatus}
                </Typography>
              </Box>
            </Box>
          </StyledTableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledTableContainer
            sx={{
              p: 2,
              height: 300,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}
            >
              Cliques
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Bar data={clicksChartData} options={chartOptions} />
            </Box>
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Status da Fila:
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: getStatusColor(clicksStatus),
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'white', fontWeight: 600 }}
                >
                  {clicksStatus}
                </Typography>
              </Box>
            </Box>
          </StyledTableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};
